import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';

import { invitesApi, type InviteListItem } from '@/api/invites';
import InviteForm from '@/features/account/InviteForm';
import RoleLegend from '@/features/account/RoleLegend';
import { formatDateTime } from '@/lib/format';
import { confirmDialog } from '@/stores/dialog';

export default function InvitesSection({ tenantId }: { tenantId: string }) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();

  const list = useQuery({
    queryKey: ['invites', tenantId],
    queryFn: () => invitesApi.list(tenantId),
  });

  const revokeMutation = useMutation({
    mutationFn: (inviteId: string) => invitesApi.revoke(tenantId, inviteId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['invites', tenantId] });
    },
  });

  return (
    <div>
      <p className="text-xs text-[var(--text-muted)] mb-4">
        {t('tenant_invites.create_link_hint')}
      </p>

      <InviteForm tenantId={tenantId} />

      {list.isLoading && <p className="text-sm text-[var(--text-muted)]">{t('common.loading')}</p>}
      {list.data && list.data.length === 0 && (
        <p className="text-sm text-[var(--text-muted)] text-center py-4">
          {t('tenant_invites.none_created')}
        </p>
      )}
      {list.data && list.data.length > 0 && (
        <div className="space-y-1">
          {list.data.map((inv) => (
            <InviteRow
              key={inv.id}
              invite={inv}
              onRevoke={async () => {
                if (await confirmDialog(t('tenant_invites.revoke_confirm', { email: inv.email }))) {
                  revokeMutation.mutate(inv.id);
                }
              }}
              busy={revokeMutation.isPending}
            />
          ))}
        </div>
      )}

      <div className="mt-5 pt-4 border-t border-[var(--border)]">
        <RoleLegend />
      </div>
    </div>
  );
}

function InviteRow({
  invite,
  onRevoke,
  busy,
}: {
  invite: InviteListItem;
  onRevoke: () => void;
  busy: boolean;
}) {
  const { t } = useTranslation();
  const statusColors: Record<string, string> = {
    pending: 'bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-300',
    accepted: 'bg-green-100 text-green-800 dark:bg-green-900/40 dark:text-green-300',
    revoked: 'bg-slate-200 text-[var(--text-muted)] dark:bg-slate-700 dark:text-[var(--text-dim)]',
    expired: 'bg-slate-200 text-[var(--text-muted)] dark:bg-slate-700 dark:text-[var(--text-dim)]',
  };
  return (
    <div className="flex items-center justify-between border border-[var(--border)] rounded px-3 py-2 text-sm">
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2">
          <span className="font-medium truncate">{invite.email}</span>
          <span className="badge bg-[var(--bg-surface)] text-[var(--text-muted)] text-[10px]">
            {invite.role}
          </span>
          <span className={`badge text-[10px] ${statusColors[invite.status]}`}>
            {invite.status}
          </span>
        </div>
        <div className="text-[10px] text-[var(--text-dim)] mt-0.5">
          {t('tenant_invites.expires_at', { when: formatDateTime(invite.expires_at) })}
        </div>
      </div>
      {invite.status === 'pending' && (
        <button
          type="button"
          className="text-xs text-red-600 hover:underline disabled:opacity-50"
          onClick={onRevoke}
          disabled={busy}
        >
          {t('common.revoke')}
        </button>
      )}
    </div>
  );
}
